// src/belediye/pages/sistem/SistemKullanicilarPage.js
//
// Sistem Yönetimi · Kullanıcılar — Başvurular kalıbı (tam ekran + PageHeader).
// - Satır/tablo formu (mobilde kart), sağda FilterSheet ile TÜM filtreler
//   (aktiflik, rol, yetki, birim, giriş, sıralama). Pagination 50.
// - Satır başında seçim tiki; sayfa + "tüm N kullanıcı" seçimi.
// - Seçim yapılınca üstte toplu işlemler: Aktif yap / Pasif yap / SMS ile şifre
//   gönder / Sil (izinlere göre, onay + sonuç bildirimi).
// Route gate: users.view. Aksiyon izinleri: users.update / users.password.reset /
// users.delete.

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  UserPlusIcon,
  ChevronRightIcon,
  BuildingOffice2Icon,
  ClockIcon,
  ShieldExclamationIcon,
  DevicePhoneMobileIcon,
  TrashIcon,
  CheckCircleIcon,
  NoSymbolIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";

import usePermissions from "../../hooks/usePermissions";
import {
  fetchSistemUsers,
  fetchRoles,
  createSistemUser,
  sendPasswordSms,
  bulkSetActive,
  bulkDeleteUsers,
} from "../../services/sistemApi";

import PageHeader from "../../components/utils/PageHeader";
import SearchBox from "../../components/utils/SearchBox";
import BasicModal from "../../components/ui/BasicModal";
import FilterSheet from "../../components/utils/FilterSheet";
import Pagination from "../../components/utils/Pagination";
import TrDateTime from "../../components/utils/TrDateTime";
import { formatPersonNameTr, titleCaseTr, safeText } from "../../components/utils/textFormat";

import "./SistemKullanicilar.css";

const PAGE_SIZE = 50;

const ROLE_LABELS = {
  başkan: "Başkan",
  personel_1: "Müdür",
  personel_2: "Halk Masası",
  birim_personeli: "Birim Personeli",
  saha_personeli: "Saha Personeli",
  personel_3: "Personel 3",
};

const displayName = (u) => formatPersonNameTr(u);

const roleText = (u, rolesByCode) => {
  if (u.global_role?.name) return u.global_role.name;
  if (u.role_ref && rolesByCode[u.role_ref]) return rolesByCode[u.role_ref];
  return ROLE_LABELS[u.role] || u.role || "Rol atanmamış";
};

const roleMatches = (u, code) =>
  !code || u.role === code || u.role_ref === code || u.global_role?.code === code;

const avatarInitial = (u) =>
  (displayName(u) || u.username || "?").trim().slice(0, 1).toLocaleUpperCase("tr");

const unitText = (u) => {
  const units = Array.isArray(u.units) ? u.units : [];
  if (units.length === 0) return "Birim atanmamış";
  const first = titleCaseTr(safeText(units[0].unit_name, "")) || `#${units[0].unit_id}`;
  return first + (units.length > 1 ? ` +${units.length - 1}` : "");
};

export default function SistemKullanicilarPage() {
  const { can } = usePermissions();
  const navigate = useNavigate();

  const canCreate = can("users.create");
  const canSms = can("users.password.reset");
  const canUpdate = can("users.update");
  const canDelete = can("users.delete");
  const canBulk = canSms || canUpdate || canDelete;

  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [q, setQ] = useState("");
  const [activeFilter, setActiveFilter] = useState("all"); // all | active | passive
  const [roleFilter, setRoleFilter] = useState("");
  const [authFilter, setAuthFilter] = useState("all"); // all | super | normal
  const [unitFilter, setUnitFilter] = useState("");
  const [loginFilter, setLoginFilter] = useState("all"); // all | logged | never
  const [sortKey, setSortKey] = useState("name_asc");

  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [showCreate, setShowCreate] = useState(false);

  // Toplu aksiyon akışı (ortak modal): type=activate|deactivate|sms|delete
  const [pendingAction, setPendingAction] = useState(null); // { type, ids }
  const [actStage, setActStage] = useState(null); // confirm | running | result
  const [actResult, setActResult] = useState({ okList: [], skipList: [] });

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [u, r] = await Promise.all([fetchSistemUsers(), fetchRoles()]);
      setUsers(Array.isArray(u) ? u : []);
      setRoles(Array.isArray(r) ? r : []);
    } catch {
      setError("Kullanıcılar yüklenemedi.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const rolesByCode = useMemo(
    () => Object.fromEntries(roles.map((r) => [r.code, r.name])),
    [roles]
  );

  const unitOptions = useMemo(() => {
    const map = new Map();
    users.forEach((u) =>
      (u.units || []).forEach((m) => {
        if (m?.unit_id != null && !map.has(m.unit_id)) {
          map.set(m.unit_id, titleCaseTr(safeText(m.unit_name, "")) || `#${m.unit_id}`);
        }
      })
    );
    return [...map.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, "tr"));
  }, [users]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLocaleLowerCase("tr");

    let list = users.filter((u) => {
      const hay = `${u.username || ""} ${u.first_name || ""} ${u.last_name || ""} ${
        u.email || ""
      } ${u.phone_number || ""}`.toLocaleLowerCase("tr");
      if (needle && !hay.includes(needle)) return false;

      const isActive = u.is_active !== false;
      if (activeFilter === "active" && !isActive) return false;
      if (activeFilter === "passive" && isActive) return false;

      if (!roleMatches(u, roleFilter)) return false;

      if (authFilter === "super" && !u.is_superuser) return false;
      if (authFilter === "normal" && u.is_superuser) return false;

      if (unitFilter) {
        const inUnit = (u.units || []).some((m) => String(m.unit_id) === String(unitFilter));
        if (!inUnit) return false;
      }

      if (loginFilter === "logged" && !u.last_login) return false;
      if (loginFilter === "never" && u.last_login) return false;

      return true;
    });

    const nameOf = (u) => (displayName(u) || u.username || "").toLocaleLowerCase("tr");
    const loginTs = (u) => (u.last_login ? new Date(u.last_login).getTime() : 0);
    list = [...list].sort((a, b) => {
      switch (sortKey) {
        case "name_desc":
          return nameOf(b).localeCompare(nameOf(a), "tr");
        case "login_desc":
          return loginTs(b) - loginTs(a);
        case "login_asc":
          return loginTs(a) - loginTs(b);
        case "name_asc":
        default:
          return nameOf(a).localeCompare(nameOf(b), "tr");
      }
    });
    return list;
  }, [users, q, activeFilter, roleFilter, authFilter, unitFilter, loginFilter, sortKey]);

  useEffect(() => {
    setPage(1);
  }, [q, activeFilter, roleFilter, authFilter, unitFilter, loginFilter, sortKey]);

  const totalCount = filtered.length;
  const paged = useMemo(
    () => filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filtered, page]
  );

  const activeFilterCount =
    (activeFilter !== "all" ? 1 : 0) +
    (roleFilter ? 1 : 0) +
    (authFilter !== "all" ? 1 : 0) +
    (unitFilter ? 1 : 0) +
    (loginFilter !== "all" ? 1 : 0) +
    (sortKey !== "name_asc" ? 1 : 0);

  const clearFilters = () => {
    setActiveFilter("all");
    setRoleFilter("");
    setAuthFilter("all");
    setUnitFilter("");
    setLoginFilter("all");
    setSortKey("name_asc");
  };

  // ── Seçim ──────────────────────────────────────────────────────────────────
  const toggleOne = (id) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const pageIds = useMemo(() => paged.map((u) => u.id), [paged]);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));
  const allFilteredSelected =
    totalCount > 0 && filtered.every((u) => selectedIds.has(u.id));

  const togglePage = () =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allPageSelected) pageIds.forEach((id) => next.delete(id));
      else pageIds.forEach((id) => next.add(id));
      return next;
    });

  const selectAllFiltered = () => setSelectedIds(new Set(filtered.map((u) => u.id)));
  const clearSelection = () => setSelectedIds(new Set());
  const selectedCount = selectedIds.size;

  // ── Toplu aksiyonlar ────────────────────────────────────────────────────────
  const ACTIONS = {
    activate: {
      label: "Aktif yap",
      icon: CheckCircleIcon,
      danger: false,
      okHead: "Aktif yapıldı",
      confirm: (n) => `${n} kullanıcı AKTİF yapılacak (giriş/erişim açılır).`,
      run: (ids) => bulkSetActive(ids, true),
      pick: (r) => r.updated || [],
    },
    deactivate: {
      label: "Pasif yap",
      icon: NoSymbolIcon,
      danger: false,
      okHead: "Pasif yapıldı",
      confirm: (n) => `${n} kullanıcı PASİF yapılacak; artık giriş yapamazlar.`,
      run: (ids) => bulkSetActive(ids, false),
      pick: (r) => r.updated || [],
    },
    sms: {
      label: "SMS ile şifre gönder",
      icon: DevicePhoneMobileIcon,
      danger: false,
      okHead: "SMS gönderildi",
      confirm: (n) =>
        `${n} kullanıcı için yeni parola üretilip telefonuna SMS ile gönderilecek. Eski parolalar geçersiz olur.`,
      run: (ids) => sendPasswordSms(ids),
      pick: (r) => r.sent || [],
    },
    delete: {
      label: "Sil",
      icon: TrashIcon,
      danger: true,
      okHead: "Silindi",
      confirm: (n) =>
        `${n} kullanıcı KALICI olarak silinecek. Bu işlem geri alınamaz!`,
      run: (ids) => bulkDeleteUsers(ids),
      pick: (r) => r.deleted || [],
    },
  };

  const openAction = (type) => {
    if (selectedCount === 0) return;
    setPendingAction({ type, ids: [...selectedIds] });
    setActStage("confirm");
  };

  const runAction = async () => {
    if (!pendingAction) return;
    const meta = ACTIONS[pendingAction.type];
    setActStage("running");
    try {
      const res = await meta.run(pendingAction.ids);
      setActResult({ okList: meta.pick(res), skipList: res.skipped || [] });
    } catch (e) {
      const msg = e?.response?.data?.error || e?.response?.data?.detail || "İşlem başarısız.";
      setActResult({
        okList: [],
        skipList: pendingAction.ids.map((id) => ({ id, reason: msg })),
      });
    }
    setActStage("result");
  };

  const closeAction = () => {
    setPendingAction(null);
    setActStage(null);
    setActResult({ okList: [], skipList: [] });
    clearSelection();
    load(); // aktiflik/silme sonrası listeyi tazele
  };

  // ── Ortak filtre alanları (FilterSheet gövdesi) ─────────────────────────────
  const filterBody = (
    <>
      <div className="afp-section">
        <div className="afp-label">Aktiflik</div>
        <select className="md-select" value={activeFilter} onChange={(e) => setActiveFilter(e.target.value)}>
          <option value="all">Tümü</option>
          <option value="active">Aktif</option>
          <option value="passive">Pasif</option>
        </select>
      </div>
      <div className="afp-section">
        <div className="afp-label">Rol</div>
        <select className="md-select" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
          <option value="">Tüm roller</option>
          {roles.map((r) => (
            <option key={r.id} value={r.code}>
              {r.name}
            </option>
          ))}
        </select>
      </div>
      <div className="afp-section">
        <div className="afp-label">Yetki türü</div>
        <select className="md-select" value={authFilter} onChange={(e) => setAuthFilter(e.target.value)}>
          <option value="all">Tümü</option>
          <option value="super">Süper kullanıcı</option>
          <option value="normal">Normal kullanıcı</option>
        </select>
      </div>
      <div className="afp-section">
        <div className="afp-label">Birim</div>
        <select className="md-select" value={unitFilter} onChange={(e) => setUnitFilter(e.target.value)}>
          <option value="">Tüm birimler</option>
          {unitOptions.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </select>
      </div>
      <div className="afp-section">
        <div className="afp-label">Giriş durumu</div>
        <select className="md-select" value={loginFilter} onChange={(e) => setLoginFilter(e.target.value)}>
          <option value="all">Tümü</option>
          <option value="logged">Giriş yapmış</option>
          <option value="never">Hiç giriş yapmamış</option>
        </select>
      </div>
      <div className="afp-section">
        <div className="afp-label">Sıralama</div>
        <select className="md-select" value={sortKey} onChange={(e) => setSortKey(e.target.value)}>
          <option value="name_asc">İsim: A → Z</option>
          <option value="name_desc">İsim: Z → A</option>
          <option value="login_desc">Son giriş: Yeni → Eski</option>
          <option value="login_asc">Son giriş: Eski → Yeni</option>
        </select>
      </div>
    </>
  );

  const columnHeader = (
    <div className="uu-colhead" aria-hidden="true">
      <div className="ucell ucheck" />
      <div className="ucell">Kullanıcı</div>
      <div className="ucell">Rol</div>
      <div className="ucell">Birim</div>
      <div className="ucell">Durum</div>
      <div className="ucell">Son giriş</div>
      <div className="ucell" />
    </div>
  );

  return (
    <div className="applications-page uu-users">
      <PageHeader
        title="Kullanıcılar"
        infoTitle="Kullanıcılar — Bilgi"
        infoContent={
          <div className="uu-info">
            <p>
              Sistemdeki tüm kullanıcıları arayın; sağdaki <strong>Filtrele</strong> ile
              aktiflik, rol, yetki, birim ve giriş durumuna göre daraltın.
            </p>
            <p>
              Satır başındaki kutucuklarla kullanıcı seçin; üstteki <strong>toplu işlemler</strong>{" "}
              ile aktif/pasif yapın, SMS ile şifre gönderin veya silin.
            </p>
          </div>
        }
      >
        <SearchBox
          value={q}
          onChange={setQ}
          placeholder="Ara — ad, kullanıcı adı, e-posta, telefon"
        />
        <FilterSheet
          activeCount={activeFilterCount}
          onClear={clearFilters}
          title="Filtrele"
          label="Filtrele"
        >
          {filterBody}
        </FilterSheet>
        {canCreate ? (
          <button
            type="button"
            className="btn-md btn-md-primary uu-new-btn"
            onClick={() => setShowCreate(true)}
          >
            <UserPlusIcon width={18} height={18} />
            <span className="uu-new-label">Yeni Kullanıcı</span>
          </button>
        ) : null}
      </PageHeader>

      {/* Seçim + toplu işlem barı */}
      {canBulk ? (
        <div className={`uu-selbar${selectedCount > 0 ? " is-active" : ""}`}>
          <label className="uu-selall">
            <input
              type="checkbox"
              checked={allPageSelected}
              onChange={togglePage}
              disabled={pageIds.length === 0}
              aria-label="Bu sayfayı seç"
            />
            <span>
              {selectedCount > 0 ? `${selectedCount} kullanıcı seçildi` : "Bu sayfayı seç"}
            </span>
          </label>

          {selectedCount > 0 && !allFilteredSelected && totalCount > PAGE_SIZE ? (
            <button type="button" className="uu-selall-more" onClick={selectAllFiltered}>
              Eşleşen tüm {totalCount} kullanıcıyı seç
            </button>
          ) : null}
          {allFilteredSelected && totalCount > PAGE_SIZE ? (
            <span className="uu-selall-note">Tüm {totalCount} kullanıcı seçili</span>
          ) : null}

          {selectedCount > 0 ? (
            <div className="uu-actions">
              {canUpdate ? (
                <>
                  <button
                    type="button"
                    className="btn-md btn-md-ghost uu-act"
                    onClick={() => openAction("activate")}
                  >
                    <CheckCircleIcon width={16} height={16} />
                    Aktif yap
                  </button>
                  <button
                    type="button"
                    className="btn-md btn-md-ghost uu-act"
                    onClick={() => openAction("deactivate")}
                  >
                    <NoSymbolIcon width={16} height={16} />
                    Pasif yap
                  </button>
                </>
              ) : null}
              {canSms ? (
                <button
                  type="button"
                  className="btn-md btn-md-ghost uu-act"
                  onClick={() => openAction("sms")}
                >
                  <DevicePhoneMobileIcon width={16} height={16} />
                  SMS ile şifre gönder
                </button>
              ) : null}
              {canDelete ? (
                <button
                  type="button"
                  className="btn-md uu-act uu-act--danger"
                  onClick={() => openAction("delete")}
                >
                  <TrashIcon width={16} height={16} />
                  Sil
                </button>
              ) : null}
              <button type="button" className="btn-md btn-md-ghost" onClick={clearSelection}>
                Vazgeç
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

      {/* İçerik */}
      {loading ? (
        <div className="md-card uu-state">
          <ClockIcon width={18} height={18} />
          Yükleniyor…
        </div>
      ) : error ? (
        <div className="md-card uu-state uu-state--error">
          <ExclamationTriangleIcon width={18} height={18} />
          <span>{error}</span>
          <button className="btn-md btn-md-ghost" onClick={load}>
            Tekrar dene
          </button>
        </div>
      ) : totalCount === 0 ? (
        <div className="md-card uu-state">
          <UsersIcon width={18} height={18} />
          {users.length === 0 ? "Henüz kullanıcı yok." : "Eşleşen kullanıcı bulunamadı."}
        </div>
      ) : (
        <>
          {columnHeader}
          <div className="uu-list">
            {paged.map((u) => {
              const isActive = u.is_active !== false;
              const selected = selectedIds.has(u.id);
              return (
                <div
                  key={u.id}
                  className={`urow${selected ? " is-selected" : ""}`}
                  onClick={() => navigate(`/yonetim/sistem/kullanicilar/${u.id}`)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") navigate(`/yonetim/sistem/kullanicilar/${u.id}`);
                  }}
                >
                  {canBulk ? (
                    <div
                      className="ucell ucheck"
                      onClick={(e) => e.stopPropagation()}
                      role="presentation"
                    >
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => toggleOne(u.id)}
                        aria-label={`${displayName(u)} seç`}
                      />
                    </div>
                  ) : (
                    <div className="ucell ucheck" />
                  )}

                  <div className="ucell uuser" data-label="Kullanıcı">
                    <span className="uu-avatar" aria-hidden="true">
                      {u.photo ? (
                        <img src={u.photo} alt="" className="uu-avatar-img" />
                      ) : (
                        avatarInitial(u)
                      )}
                    </span>
                    <span className="uu-id">
                      <span className="uu-name">{displayName(u)}</span>
                      <span className="uu-username">@{u.username}</span>
                    </span>
                  </div>

                  <div className="ucell urole" data-label="Rol">
                    <span className="md-chip md-chip-info">{roleText(u, rolesByCode)}</span>
                  </div>

                  <div className="ucell uunit" data-label="Birim">
                    <span className="uu-unit">
                      <BuildingOffice2Icon width={14} height={14} />
                      {unitText(u)}
                    </span>
                  </div>

                  <div className="ucell ustatus" data-label="Durum">
                    <span className={`md-chip ${isActive ? "md-chip-success" : "md-chip-danger"}`}>
                      {isActive ? "Aktif" : "Pasif"}
                    </span>
                    {u.is_superuser ? (
                      <span className="md-chip md-chip-danger uu-chip-super" title="Süper kullanıcı">
                        <ShieldExclamationIcon width={12} height={12} />
                        Süper
                      </span>
                    ) : null}
                  </div>

                  <div className="ucell ulogin" data-label="Son giriş">
                    {u.last_login ? (
                      <span className="uu-login">
                        <ClockIcon width={14} height={14} />
                        <TrDateTime value={u.last_login} />
                      </span>
                    ) : (
                      <span className="uu-login uu-login--never">Hiç giriş yapmadı</span>
                    )}
                  </div>

                  <div className="ucell uchev" aria-hidden="true">
                    <ChevronRightIcon width={18} height={18} />
                  </div>
                </div>
              );
            })}
          </div>

          <Pagination
            page={page}
            totalCount={totalCount}
            pageSize={PAGE_SIZE}
            shownCount={paged.length}
            onChange={setPage}
          />
        </>
      )}

      {/* Yeni kullanıcı modalı */}
      {showCreate && (
        <SistemUserCreateModal
          onClose={() => {
            setShowCreate(false);
            load();
          }}
          onCreated={(created) => {
            setShowCreate(false);
            if (created?.id) navigate(`/yonetim/sistem/kullanicilar/${created.id}`);
            else load();
          }}
        />
      )}

      {/* Toplu işlem modalı: onay / çalışıyor / sonuç */}
      {actStage && pendingAction && (
        <ActionModal
          meta={ACTIONS[pendingAction.type]}
          count={pendingAction.ids.length}
          stage={actStage}
          result={actResult}
          onConfirm={runAction}
          onClose={closeAction}
        />
      )}
    </div>
  );
}

// ── Toplu işlem modalı (onay → çalışıyor → sonuç) ─────────────────────────────
function ActionModal({ meta, count, stage, result, onConfirm, onClose }) {
  const Icon = meta.icon;
  const actions =
    stage === "confirm" ? (
      <>
        <button type="button" className="btn-md btn-md-ghost" onClick={onClose}>
          Vazgeç
        </button>
        <button
          type="button"
          className={`btn-md ${meta.danger ? "uu-act--danger" : "btn-md-primary"}`}
          onClick={onConfirm}
        >
          <Icon width={16} height={16} />
          {meta.label} ({count})
        </button>
      </>
    ) : stage === "result" ? (
      <button type="button" className="btn-md btn-md-primary" onClick={onClose}>
        Kapat
      </button>
    ) : null;

  return (
    <BasicModal
      open
      onClose={onClose}
      size="md"
      title={stage === "result" ? "İşlem sonucu" : meta.label}
      showClose={stage !== "running"}
      closeOnBackdrop={stage !== "running"}
      actions={actions}
    >
      {stage === "confirm" && (
        <div className={`uu-confirm${meta.danger ? " is-danger" : ""}`}>
          {meta.danger ? (
            <ExclamationTriangleIcon width={20} height={20} />
          ) : (
            <Icon width={20} height={20} />
          )}
          <p>{meta.confirm(count)}</p>
        </div>
      )}

      {stage === "running" && (
        <div className="uu-running">
          <ArrowPathIcon width={22} height={22} className="uu-spin" />
          <span>İşlem uygulanıyor…</span>
        </div>
      )}

      {stage === "result" && (
        <div className="syk-form">
          {result.okList.length > 0 ? (
            <div className="uu-result-block">
              <div className="uu-result-head uu-result-head--ok">
                <CheckCircleIcon width={16} height={16} />
                {meta.okHead} ({result.okList.length})
              </div>
              <ul className="uu-result-list">
                {result.okList.map((r) => (
                  <li key={`ok-${r.id}`} className="uu-result">
                    <span className="uu-result-user">@{r.username || r.id}</span>
                    {r.phone ? <span className="uu-result-info">{r.phone}</span> : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {result.skipList.length > 0 ? (
            <div className="uu-result-block">
              <div className="uu-result-head uu-result-head--warn">
                <ExclamationTriangleIcon width={16} height={16} />
                Uygulanamayan ({result.skipList.length})
              </div>
              <ul className="uu-result-list">
                {result.skipList.map((r, i) => (
                  <li key={`sk-${r.id}-${i}`} className="uu-result is-error">
                    <span className="uu-result-user">{r.username ? `@${r.username}` : `#${r.id}`}</span>
                    <span className="uu-result-err">{r.reason}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {result.okList.length === 0 && result.skipList.length === 0 ? (
            <p className="syk-created-note">İşlem tamamlandı.</p>
          ) : null}
        </div>
      )}
    </BasicModal>
  );
}

// Yeni kullanıcı = hesap alanları (telefon ZORUNLU). Parolayı backend üretir
// (generated_password) — oluşturunca gösterilir. Rol + birim ataması detayda.
function SistemUserCreateModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    username: "",
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    setSaving(true);
    setErr("");
    try {
      const created = await createSistemUser(form);
      setResult(created);
    } catch (ex) {
      const d = ex?.response?.data;
      setErr(
        typeof d === "string"
          ? d
          : d
          ? Object.entries(d)
              .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`)
              .join(" · ")
          : "Kullanıcı oluşturulamadı."
      );
    } finally {
      setSaving(false);
    }
  };

  const actions = result ? (
    <>
      <button type="button" className="btn-md btn-md-ghost" onClick={onClose}>
        Kapat
      </button>
      <button type="button" className="btn-md btn-md-primary" onClick={() => onCreated(result)}>
        Rol ve birim ata
      </button>
    </>
  ) : (
    <>
      <button type="button" className="btn-md btn-md-ghost" onClick={onClose}>
        Vazgeç
      </button>
      <button type="button" className="btn-md btn-md-primary" onClick={submit} disabled={saving}>
        {saving ? "Oluşturuluyor…" : "Oluştur"}
      </button>
    </>
  );

  return (
    <BasicModal
      open
      onClose={onClose}
      size="md"
      title={result ? "Kullanıcı oluşturuldu" : "Yeni Kullanıcı"}
      actions={actions}
    >
      {result ? (
        <div className="syk-form">
          <p className="syk-created-note">
            <strong>{result.username}</strong> oluşturuldu. Geçici parolayı kullanıcıya iletin —
            bu ekrandan sonra tekrar gösterilmez.
          </p>
          <div className="syk-pass-box">
            <span className="syk-pass-label">Geçici parola</span>
            <code className="syk-pass-value">{result.generated_password || "—"}</code>
          </div>
        </div>
      ) : (
        <form
          className="syk-form"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
          autoComplete="off"
        >
          {err && <div className="syk-alert">{err}</div>}
          <label className="syk-field">
            <span>Kullanıcı adı</span>
            <input className="md-input" value={form.username} onChange={set("username")} required autoFocus />
          </label>
          <div className="syk-field-row">
            <label className="syk-field">
              <span>Ad</span>
              <input className="md-input" value={form.first_name} onChange={set("first_name")} />
            </label>
            <label className="syk-field">
              <span>Soyad</span>
              <input className="md-input" value={form.last_name} onChange={set("last_name")} />
            </label>
          </div>
          <label className="syk-field">
            <span>Telefon</span>
            <input
              className="md-input"
              type="tel"
              inputMode="numeric"
              value={form.phone_number}
              onChange={set("phone_number")}
              placeholder="5xxxxxxxxx"
              required
            />
          </label>
          <label className="syk-field">
            <span>E-posta (opsiyonel)</span>
            <input className="md-input" type="email" value={form.email} onChange={set("email")} />
          </label>
          {/* Enter ile gönderimi korumak için gizli submit */}
          <button type="submit" className="syk-hidden-submit" tabIndex={-1} aria-hidden="true" />
        </form>
      )}
    </BasicModal>
  );
}
